import { useMutation, useQuery } from "@apollo/client";
import ArrowBack from "@mui/icons-material/ArrowBack";
import PublishIcon from "@mui/icons-material/Publish";
import Search from "@mui/icons-material/Search";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Divider,
  IconButton,
  Stack,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableRow,
  Tabs,
  Tooltip,
  Typography,
} from "@mui/material";
import { GridColDef, GridRenderCellParams } from "@mui/x-data-grid-premium";
import { ConfirmDialog } from "components/ConfirmDialog";
import { DataGrid } from "components/DataGrid";
import { S3FileLink } from "components/S3FileLink";
import {
  WastePipelineRunMetadataQuery,
  WastePipelineRunRowsQuery,
  WastePipelineRunRejectedRowsQuery,
} from "generated-graphql/graphql";
import { useBreadcrumb } from "hooks/useBreadcrumbs";
import { inProgressJobStatuses } from "hooks/useLongPollingForJob";
import { useCallback, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";

import {
  INGEST_WASTE_PIPELINE_DATA_MUTATION,
  WASTE_PIPELINE_RUN_METADATA_QUERY,
  WASTE_PIPELINE_RUN_ROWS_QUERY,
  WASTE_PIPELINE_RUN_REJECTED_ROWS_QUERY,
} from "./api";
import { FacilityMatchDialog } from "./FacilityMatchDialog";
import { getIngestionAlertSeverity, getIngestionStatusColor } from "./pipelineUtils";

type ReviewRowBase = WastePipelineRunRowsQuery["wastePipelineRunRows"]["items"][number];
type RejectedRowBase =
  WastePipelineRunRejectedRowsQuery["wastePipelineRunRejectedRows"]["items"][number];
type ReviewRow = ReviewRowBase & { _rowId: number };
type RejectedRow = RejectedRowBase & { _rowId: number };

export const PipelineRunResults = () => {
  const { pipelineId } = useParams<{ pipelineId: string }>();
  const [activeTab, setActiveTab] = useState(0);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [ingestResult, setIngestResult] = useState<{ jobId: string } | null>(null);
  const [ingestError, setIngestError] = useState<string | null>(null);
  const [facilityDialogRow, setFacilityDialogRow] = useState<RejectedRow | null>(null);

  // Metadata + pipeline info
  const {
    data: metadataData,
    loading: metadataLoading,
    refetch: refetchMetadata,
  } = useQuery(WASTE_PIPELINE_RUN_METADATA_QUERY, {
    variables: { pipelineId: pipelineId ?? "" },
    skip: !pipelineId,
  });

  const metadata = metadataData?.wastePipelineRunMetadata;
  const pipeline = metadataData?.pipeline;

  // All matched rows (no skip/take — return everything for client-side pagination)
  const { data: rowsData, loading: rowsLoading } = useQuery(WASTE_PIPELINE_RUN_ROWS_QUERY, {
    variables: { pipelineId: pipelineId ?? "" },
    skip: !pipelineId || activeTab !== 0,
  });

  // All rejected rows (no skip/take)
  const { data: rejectedData, loading: rejectedLoading } = useQuery(
    WASTE_PIPELINE_RUN_REJECTED_ROWS_QUERY,
    {
      variables: { pipelineId: pipelineId ?? "" },
      skip: !pipelineId || activeTab !== 1,
    }
  );

  const [ingest, { loading: ingestLoading }] = useMutation(INGEST_WASTE_PIPELINE_DATA_MUTATION);

  const handleIngestConfirm = useCallback(async () => {
    if (!pipelineId) return;
    try {
      const result = await ingest({
        variables: { pipelineId },
      });
      const jobId = result.data?.ingestWastePipelineData?.id;
      if (jobId) {
        setIngestResult({ jobId });
      }
      setIngestError(null);
      setConfirmOpen(false);
      refetchMetadata();
    } catch (err) {
      setIngestError(err instanceof Error ? err.message : "Ingestion failed");
      setConfirmOpen(false);
    }
  }, [pipelineId, ingest, refetchMetadata]);

  const ingestionJobInProgress =
    !!pipeline?.ingestionJob?.status &&
    inProgressJobStatuses.includes(pipeline.ingestionJob.status);

  const pipelineName = pipeline?.name ?? "Pipeline";

  useBreadcrumb([
    { label: "Pipelines", to: "/staff/pipelines" },
    { label: pipelineName },
    { label: "Results" },
  ]);

  const reviewColumns = useReviewColumns();
  const rejectedColumns = useRejectedColumns(setFacilityDialogRow);

  // Add synthetic row IDs for stable DataGrid keys
  const reviewRows = useMemo(
    () =>
      (rowsData?.wastePipelineRunRows.items ?? []).map((row, i) => ({
        ...row,
        _rowId: i,
      })),
    [rowsData]
  );

  const rejectedRows = useMemo(
    () =>
      (rejectedData?.wastePipelineRunRejectedRows.items ?? []).map((row, i) => ({
        ...row,
        _rowId: i,
      })),
    [rejectedData]
  );

  if (!pipelineId) {
    return <Typography>No pipeline ID provided.</Typography>;
  }

  if (metadataLoading) {
    return (
      <Stack alignItems="center" justifyContent="center" py={8}>
        <CircularProgress />
      </Stack>
    );
  }

  if (!metadata) {
    return (
      <Stack spacing={2} py={3} px={2}>
        <Button
          component={Link}
          to="/staff/pipelines"
          startIcon={<ArrowBack />}
          sx={{ alignSelf: "flex-start" }}
        >
          Back to Pipelines
        </Button>
        <Alert severity="info">
          No review data available for this pipeline run. The pipeline may not have produced a
          reviewable output.
        </Alert>
      </Stack>
    );
  }

  return (
    <Stack spacing={3} py={3} px={2}>
      {/* Header */}
      <Stack direction="row" alignItems="center" spacing={2}>
        <Button component={Link} to="/staff/pipelines" startIcon={<ArrowBack />}>
          Back to Pipelines
        </Button>
        <Typography variant="h5">{pipelineName} — Results</Typography>
        {pipeline?.tenant?.name && (
          <Typography variant="subtitle1" color="text.secondary">
            {pipeline.tenant.name}
          </Typography>
        )}
        <Box sx={{ flex: 1 }} />
        <Button
          variant="contained"
          startIcon={<PublishIcon />}
          onClick={() => setConfirmOpen(true)}
          disabled={ingestLoading || ingestionJobInProgress || (metadata?.matchedRows ?? 0) === 0}
        >
          Ingest Pipeline
        </Button>
      </Stack>

      {ingestResult && (
        <Alert severity="success" onClose={() => setIngestResult(null)}>
          Ingestion job created (ID: {ingestResult.jobId}). Check the Jobs page for status.
        </Alert>
      )}
      {ingestError && (
        <Alert severity="error" onClose={() => setIngestError(null)}>
          {ingestError}
        </Alert>
      )}

      {pipeline?.ingestionJob && (
        <IngestionStatusBanner
          jobId={pipeline.ingestionJob.id}
          status={pipeline.ingestionJob.status}
          ingestedAt={pipeline.ingestedAt}
        />
      )}

      <ConfirmDialog
        open={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        onConfirm={handleIngestConfirm}
        loading={ingestLoading}
        header="Ingest Pipeline Data"
        info={
          pipeline?.ingestedAt
            ? `This pipeline was previously ingested on ${new Date(pipeline.ingestedAt).toLocaleString()}. Re-ingesting will overwrite existing data.`
            : `This will import ${(metadata?.matchedRows ?? 0).toLocaleString()} matched rows into the system for ${pipeline?.tenant?.name ?? "this tenant"}.`
        }
        confirmText="Ingest"
        confirmStartIcon={<PublishIcon />}
      />

      {/* Summary Stats */}
      <MetadataSummary metadata={metadata} />

      {/* Tabs: Matched Rows / Rejected Rows / Output Files */}
      <Box>
        <Tabs
          value={activeTab}
          onChange={(_, newValue) => setActiveTab(newValue)}
          sx={{ borderBottom: 1, borderColor: "divider" }}
        >
          <Tab label={`Matched Rows (${metadata.matchedRows.toLocaleString()})`} />
          <Tab label={`Rejected Rows (${metadata.rejectedRowCount.toLocaleString()})`} />
          <Tab label="Output Files" />
        </Tabs>

        {activeTab === 0 && (
          <Box sx={{ height: "calc(100vh - 480px)", minHeight: 400, mt: 2 }}>
            <DataGrid
              rows={reviewRows}
              columns={reviewColumns}
              loading={rowsLoading}
              pageSizeOptions={[50, 100, 250, 500]}
              initialState={{ pagination: { paginationModel: { pageSize: 100 } } }}
              getRowId={(row) => row._rowId}
              disableRowSelectionOnClick
              noRowsMessage="No matched rows found"
            />
          </Box>
        )}

        {activeTab === 1 && (
          <Box sx={{ height: "calc(100vh - 480px)", minHeight: 400, mt: 2 }}>
            <DataGrid
              rows={rejectedRows}
              columns={rejectedColumns}
              loading={rejectedLoading}
              pageSizeOptions={[50, 100, 250, 500]}
              initialState={{ pagination: { paginationModel: { pageSize: 100 } } }}
              getRowId={(row) => row._rowId}
              getDetailPanelContent={getDetailPanelContent}
              getDetailPanelHeight={() => "auto"}
              disableRowSelectionOnClick
              noRowsMessage="No rejected rows found"
            />
          </Box>
        )}

        {activeTab === 2 && (
          <Box sx={{ mt: 2 }}>
            {pipeline?.outputFiles && pipeline.outputFiles.length > 0 ? (
              <Stack spacing={1}>
                {pipeline.outputFiles.map((fileKey) => (
                  <S3FileLink key={fileKey} fileKey={fileKey} />
                ))}
              </Stack>
            ) : (
              <Typography color="text.secondary">No output files available.</Typography>
            )}
          </Box>
        )}
      </Box>

      {/* Facility match dialog */}
      {facilityDialogRow?.extractedAddress && pipeline?.tenantId && (
        <FacilityMatchDialog
          open
          onClose={() => setFacilityDialogRow(null)}
          extractedAddress={facilityDialogRow.extractedAddress}
          tenantId={pipeline.tenantId}
        />
      )}
    </Stack>
  );
};

// ---------------------------------------------------------------------------
// Metadata Summary — single compact card
// ---------------------------------------------------------------------------

type MetadataType = NonNullable<WastePipelineRunMetadataQuery["wastePipelineRunMetadata"]>;

function MetadataSummary({ metadata }: { metadata: MetadataType }) {
  const { stats } = metadata;
  return (
    <Card variant="outlined">
      <CardContent sx={{ py: 2, "&:last-child": { pb: 2 } }}>
        {/* Run info */}
        <Stack direction="row" spacing={3} sx={{ mb: 1.5 }}>
          <Typography variant="caption" color="text.secondary">
            Vendor: <strong>{metadata.vendorSource}</strong>
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Run Key: <strong>{metadata.runKey}</strong>
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Generated: <strong>{new Date(metadata.generatedAt).toLocaleString()}</strong>
          </Typography>
        </Stack>

        <Divider sx={{ mb: 1.5 }} />

        {/* Key counts + classification side by side */}
        <Stack direction="row" spacing={4} flexWrap="wrap" useFlexGap>
          {/* Counts */}
          <Box>
            <Typography
              variant="overline"
              color="text.secondary"
              sx={{ mb: 0.5, display: "block" }}
            >
              Row Counts
            </Typography>
            <Table size="small" sx={{ "& td, & th": { border: 0, py: 0.25, px: 1 } }}>
              <TableBody>
                <StatRow label="Source Rows" value={metadata.sourceRows} />
                <StatRow label="Matched" value={metadata.matchedRows} />
                <StatRow label="Rejected" value={metadata.rejectedRowCount} />
                <StatRow label="Unique Manifests" value={stats.uniqueManifests} />
                <StatRow label="Unique Facilities" value={stats.uniqueFacilities} />
              </TableBody>
            </Table>
          </Box>

          {/* Classification */}
          <Box>
            <Typography
              variant="overline"
              color="text.secondary"
              sx={{ mb: 0.5, display: "block" }}
            >
              Classification
            </Typography>
            <Table size="small" sx={{ "& td, & th": { border: 0, py: 0.25, px: 1 } }}>
              <TableBody>
                <StatRow label="RCRA Hazardous" value={stats.classificationRcraHazardous} />
                <StatRow
                  label="Acutely Hazardous"
                  value={stats.classificationRcraAcutelyHazardous}
                />
                <StatRow label="State Regulated" value={stats.classificationStateRegulated} />
                <StatRow label="Nonhazardous" value={stats.classificationNonhazardous} />
              </TableBody>
            </Table>
          </Box>

          {/* Reportability */}
          <Box>
            <Typography
              variant="overline"
              color="text.secondary"
              sx={{ mb: 0.5, display: "block" }}
            >
              Reportability
            </Typography>
            <Table size="small" sx={{ "& td, & th": { border: 0, py: 0.25, px: 1 } }}>
              <TableBody>
                <StatRow label="Reportable" value={stats.reportable} />
                <StatRow label="Not Reportable" value={stats.notReportable} />
              </TableBody>
            </Table>
          </Box>
        </Stack>
      </CardContent>
    </Card>
  );
}

function StatRow({ label, value }: { label: string; value: number }) {
  return (
    <TableRow>
      <TableCell sx={{ color: "text.secondary" }}>{label}</TableCell>
      <TableCell align="right">
        <strong>{value.toLocaleString()}</strong>
      </TableCell>
    </TableRow>
  );
}

// ---------------------------------------------------------------------------
// Ingestion status banner
// ---------------------------------------------------------------------------

function IngestionStatusBanner({
  jobId,
  status,
  ingestedAt,
}: {
  jobId: string;
  status: string;
  ingestedAt?: string | null;
}) {
  const severity = getIngestionAlertSeverity(status);

  return (
    <Alert severity={severity} icon={false} sx={{ alignItems: "center" }}>
      <Stack direction="row" spacing={2} alignItems="center">
        <Typography variant="body2">
          <strong>Ingestion:</strong>
        </Typography>
        <Chip size="small" label={status} color={getIngestionStatusColor(status)} />
        <Typography variant="body2" color="text.secondary">
          Job: {jobId}
        </Typography>
        {ingestedAt && status === "SUCCEEDED" && (
          <Typography variant="body2" color="text.secondary">
            Completed: {new Date(ingestedAt).toLocaleString()}
          </Typography>
        )}
      </Stack>
    </Alert>
  );
}

// ---------------------------------------------------------------------------
// Rejected row detail panel (shows rawData JSON)
// ---------------------------------------------------------------------------

function getDetailPanelContent({ row }: { row: RejectedRow }) {
  if (!row.rawData) return null;
  return (
    <Box sx={{ p: 2, bgcolor: "grey.50" }}>
      <Typography variant="subtitle2" gutterBottom>
        Original Row Data
      </Typography>
      <Box
        component="pre"
        sx={{
          fontSize: "0.75rem",
          maxHeight: 400,
          overflow: "auto",
          bgcolor: "grey.100",
          p: 1.5,
          borderRadius: 1,
          whiteSpace: "pre-wrap",
          wordBreak: "break-word",
        }}
      >
        {JSON.stringify(row.rawData, null, 2)}
      </Box>
    </Box>
  );
}

// ---------------------------------------------------------------------------
// Column definitions
// ---------------------------------------------------------------------------

function useReviewColumns(): GridColDef<ReviewRow>[] {
  return useMemo(
    () => [
      { field: "manifestTrackingNumber", headerName: "Manifest #", width: 180 },
      { field: "shippedDate", headerName: "Shipped Date", width: 120 },
      { field: "receivedDate", headerName: "Received Date", width: 120 },
      { field: "facilityId", headerName: "Facility ID", width: 140 },
      { field: "generatorId", headerName: "Generator ID", width: 140 },
      { field: "generatorName", headerName: "Generator Name", width: 180 },
      { field: "generatorLocationStreetNo", headerName: "Gen Street No", width: 120 },
      { field: "generatorLocationStreet1", headerName: "Gen Street 1", width: 160 },
      { field: "generatorLocationStreet2", headerName: "Gen Street 2", width: 160 },
      { field: "generatorLocationCity", headerName: "Gen City", width: 130 },
      { field: "generatorLocationZip", headerName: "Gen Zip", width: 100 },
      { field: "generatorLocationState", headerName: "Gen State", width: 100 },
      { field: "desFacilityId", headerName: "Des Facility ID", width: 140 },
      { field: "desFacilityName", headerName: "Des Facility Name", width: 180 },
      { field: "vendorSource", headerName: "Vendor Source", width: 130 },
      { field: "vendorSiteId", headerName: "Vendor Site ID", width: 130 },
      { field: "wasteLineNumber", headerName: "Waste Line #", width: 120 },
      { field: "dotHazardous", headerName: "DOT Hazardous", width: 130 },
      { field: "dotIdNumberDescription", headerName: "DOT ID/Desc", width: 160 },
      { field: "dotPrintedInformation", headerName: "DOT Printed Info", width: 160 },
      { field: "nonHazWasteDescription", headerName: "Non-Haz Description", width: 180 },
      { field: "containerNumber", headerName: "Container #", width: 110, type: "number" },
      { field: "containerTypeCode", headerName: "Container Type", width: 120 },
      { field: "quantity", headerName: "Quantity", width: 110 },
      { field: "qtyUnitOfMeasureCode", headerName: "UOM Code", width: 110 },
      { field: "managementMethodCode", headerName: "Mgmt Method", width: 130 },
      { field: "managementMethodDescription", headerName: "Mgmt Method Desc", width: 180 },
      { field: "finalManagementMethodCode", headerName: "Final Mgmt Method", width: 150 },
      { field: "finalManagementMethodDescription", headerName: "Final Mgmt Desc", width: 180 },
      { field: "brDensity", headerName: "BR Density", width: 110 },
      { field: "brDensityUomCode", headerName: "BR Density UOM", width: 130 },
      { field: "brDensityUomDescription", headerName: "BR Density UOM Desc", width: 160 },
      { field: "brFormCode", headerName: "BR Form Code", width: 120 },
      { field: "brSourceCode", headerName: "BR Source Code", width: 130 },
      { field: "epaWaste", headerName: "EPA Waste", width: 110 },
      { field: "federalWasteCodes", headerName: "Federal Waste Codes", width: 160 },
      { field: "stateWasteCodes", headerName: "State Waste Codes", width: 150 },
      { field: "vendorProfileNumber", headerName: "Vendor Profile #", width: 140 },
      { field: "vendorProfileDescription", headerName: "Vendor Profile Desc", width: 180 },
      { field: "vendorComments", headerName: "Vendor Comments", width: 180 },
      { field: "classification", headerName: "Classification", width: 160 },
      { field: "transporter1Id", headerName: "Transporter 1 ID", width: 140 },
      { field: "transporter1Name", headerName: "Transporter 1 Name", width: 160 },
      {
        field: "transporter1DaysInPossession",
        headerName: "Trans 1 Days",
        width: 110,
        type: "number",
      },
      { field: "transporter2Id", headerName: "Transporter 2 ID", width: 140 },
      { field: "transporter2Name", headerName: "Transporter 2 Name", width: 160 },
      {
        field: "transporter2DaysInPossession",
        headerName: "Trans 2 Days",
        width: 110,
        type: "number",
      },
      { field: "transporter3Id", headerName: "Transporter 3 ID", width: 140 },
      { field: "transporter3Name", headerName: "Transporter 3 Name", width: 160 },
      {
        field: "transporter3DaysInPossession",
        headerName: "Trans 3 Days",
        width: 110,
        type: "number",
      },
      { field: "transporter4Id", headerName: "Transporter 4 ID", width: 140 },
      { field: "transporter4Name", headerName: "Transporter 4 Name", width: 160 },
      {
        field: "transporter4DaysInPossession",
        headerName: "Trans 4 Days",
        width: 110,
        type: "number",
      },
      { field: "tags", headerName: "Tags", width: 130 },
      { field: "reportable", headerName: "Reportable", width: 120 },
      { field: "overrideManualEdits", headerName: "Override Manual Edits", width: 160 },
    ],
    []
  );
}

function formatExtractedAddress(addr: RejectedRow["extractedAddress"]): string {
  if (!addr) return "—";
  const parts: string[] = [];
  if (addr.name) parts.push(addr.name);

  const location: string[] = [];
  if (addr.streetAddress) location.push(addr.streetAddress);
  if (addr.city) location.push(addr.city);
  if (addr.state && addr.zip) {
    location.push(`${addr.state} ${addr.zip}`);
  } else if (addr.state) {
    location.push(addr.state);
  } else if (addr.zip) {
    location.push(addr.zip);
  }

  if (location.length > 0) {
    parts.push(location.join(", "));
  }
  if (addr.epaId) parts.push(`EPA: ${addr.epaId}`);
  if (addr.vendorCode) parts.push(`Code: ${addr.vendorCode}`);

  return parts.join(" — ") || "—";
}

function useRejectedColumns(onFindFacility: (row: RejectedRow) => void): GridColDef<RejectedRow>[] {
  return useMemo(
    () => [
      { field: "rejectionReason", headerName: "Rejection Reason", flex: 1, minWidth: 300 },
      { field: "rejectionCategory", headerName: "Category", width: 180 },
      {
        field: "extractedAddress",
        headerName: "Address",
        width: 300,
        renderCell: (params: GridRenderCellParams<RejectedRow>) =>
          formatExtractedAddress(params.row.extractedAddress),
      },
      {
        field: "_actions",
        headerName: "",
        width: 120,
        sortable: false,
        filterable: false,
        renderCell: (params: GridRenderCellParams<RejectedRow>) => {
          if (!params.row.extractedAddress) return null;
          return (
            <Tooltip title="Search for matching facility">
              <IconButton size="small" color="primary" onClick={() => onFindFacility(params.row)}>
                <Search fontSize="small" />
              </IconButton>
            </Tooltip>
          );
        },
      },
      { field: "sourceFile", headerName: "Source File", width: 200 },
      { field: "sourceLineNumber", headerName: "Line #", width: 100, type: "number" },
    ],
    [onFindFacility]
  );
}
